<script setup lang="ts">
import { computed } from 'vue'
import useProductSharing from '@/legacy/ts/useProductSharing'
import {
	getProductSharingUrl,
	productSharingData,
} from '@/legacy/ts/productSharing'
import BsButtonNormal from '@/legacy/components/ButtonNormal.vue'
import BsButtonOutline from '@/legacy/components/ButtonOutline.vue'
import injectStrict from '@/lib/inject'
import { instanceProductSharing } from '@/assets/ts/apiManagement'
import { getVueAssetImagePath } from '@/lib/utils'

const { isHasPSProfile } = useProductSharing()
const props = defineProps({
	userInfo: {
		type: Object,
		default: null,
	},
	isLoading: {
		type: Boolean,
		default: false,
	},
	text: {
		type: Object,
	},
	selfProfileName: {
		type: String,
		default: '',
	},
})
const addMessage: (message: string) => void = injectStrict('addMessage')
const emit = defineEmits(['update'])

const userAvatar = computed(() => {
	if (!props.userInfo || !props.userInfo.image) {
		return getVueAssetImagePath('avatar_2_48px@2x.png')
	}
	return props.userInfo.image.content
})

const isNotSelf = computed(() => {
	return props.userInfo.username !== props.selfProfileName
})

function updateProfileFollowFlag() {
	if (!productSharingData.isLogin) {
		window.BsHeaderVm.showPanel('login')
		return
	}

	if (props.selfProfileName) {
		const actionName = props.userInfo.isFollowed ? 'unfollow' : 'follow'
		const PStoken =
			localStorage &&
			localStorage.getItem(`tokenProductSharing_${productSharingData.userId}`)
		const suffix = props.userInfo.isFollowed ? 'unfollow' : 'v2/follow'
		instanceProductSharing
			.post(
				`${productSharingData.apiUrl}/${suffix}`,
				{ username: decodeURIComponent(props.userInfo.username) },
				{ headers: { Authorization: `Bearer ${PStoken}` } },
			)
			.then(({ data: { data } }) => {
				emit('update', {
					name: data.user.username,
					val: data.user.is_followed,
				})
				addMessage(props.text?.msg[actionName].success)
			})
			.catch(err => {
				if (err.response && err.response.status === 401) {
					isHasPSProfile().then(() => updateProfileFollowFlag())
				} else {
					addMessage(props.text?.msg[actionName].fail)
				}
			})
	} else window.location.href = getProductSharingUrl('private-index')
}

function messageCardAction() {
	window.location.href = getProductSharingUrl('profile', {
		name: props.userInfo.username,
	})
}
</script>

<template>
	<div
		class="mb-[12px] flex cursor-pointer items-center px-0 py-[16px]"
		@click="messageCardAction"
	>
		<div class="inline-block h-[56px] w-[56px]">
			<template v-if="isLoading">
				<div
					class="h-[56px] w-[56px] animate-pulse rounded-full bg-[#f6f7f8]"
				></div>
			</template>
			<template v-else>
				<img
					v-if="userAvatar"
					class="h-[56px] w-[56px] rounded-full object-cover"
					:src="userAvatar"
					alt="userAvatar"
				/>
				<img
					v-else
					class="h-[56px] w-[56px] rounded-full object-cover"
					:src="getVueAssetImagePath('avatar_2_48px@2x.png')"
					alt="userAvatar"
				/>
			</template>
		</div>
		<div :class="['ml-[8px] flex w-full flex-1 flex-col', { flex: isLoading }]">
			<span
				:class="[
					'mb-[5px] break-words text-[16px] font-medium leading-[24px] tracking-[0.15px] text-[rgba(38,38,38,0.87)]',
					{
						'rounded h-[24px] w-[60%] animate-pulse bg-[#f6f7f8]': isLoading,
					},
				]"
			>
				<template v-if="!isLoading">
					{{ userInfo.username }}
				</template>
			</span>
			<div
				:class="[
					'inline',
					{
						'rounded h-[24px] w-[80%] animate-pulse bg-[#f6f7f8]': isLoading,
					},
				]"
			>
				<span v-if="!isLoading" class="bs-contributions">
					{{ userInfo.totalContributions }}
					{{ text?.contributions }}
				</span>
			</div>
		</div>
		<div v-if="!isLoading" class="flex items-center">
			<BsButtonOutline
				v-if="isNotSelf && userInfo.isFollowed"
				:action="updateProfileFollowFlag"
				:text="text?.button.unFollow"
				button-class="inline-flex w-[122px] items-center justify-center rounded-[20px]"
			></BsButtonOutline>
			<BsButtonNormal
				v-if="isNotSelf && !userInfo.isFollowed"
				:action="updateProfileFollowFlag"
				:text="text?.button.follow"
				button-class="inline-flex w-[122px] items-center justify-center rounded-[20px]"
				wrapper-class="ml-[16px] min-h-0 lgOld:ml-[48px]"
			></BsButtonNormal>
		</div>
	</div>
</template>
